import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import { prepareFreeWorkoutExercises } from './workoutPreparation';
import { copyFitnessExerciseMetadata, type FitnessExerciseMetadata, type FitnessSetType } from '../../../supabase/functions/_shared/fitnessWorkoutMetadata';
import { getFreeWorkoutExerciseChoices, type FreeWorkoutSelections } from './freeWorkoutSelection';
import type { MuscleGroupKey } from '@/lib/exerciseLibrary';

const orderKey = 'vyv_free_workout_order';
const builderKey = (ownerId: string) => 'vyv_free_workout_builder:' + ownerId;
export interface FreeWorkoutItem extends FitnessExerciseMetadata {
  name: string; muscle: string; sets: number; defaultReps: number; defaultWeight: number;
  timed: boolean; restSeconds: number; setTypes?: FitnessSetType[];
}
export function orderedFreeWorkoutIds(order: unknown, selections: FreeWorkoutSelections, groups: readonly MuscleGroupKey[]): string[] {
  const available = groups.flatMap(group => getFreeWorkoutExerciseChoices(group)
    .filter(exercise => selections[group]?.includes(exercise.id)).map(exercise => exercise.id));
  return [...new Set([...(Array.isArray(order) ? order.filter((id): id is string => typeof id === 'string' && available.includes(id)) : []), ...available])];
}
export function readFreeWorkoutOrder(): unknown {
  try { return JSON.parse(sessionStorage.getItem(orderKey) ?? '[]'); } catch { return []; }
}
export function saveFreeWorkoutOrder(order: readonly string[]): void {
  try { sessionStorage.setItem(orderKey, JSON.stringify(order)); } catch { /* Route state remains available. */ }
}
export function clearFreeWorkoutOrder(): void {
  try { sessionStorage.removeItem(orderKey); } catch { /* The next selection is normalized. */ }
}
export function normalizeFreeWorkoutItems(value: unknown): FreeWorkoutItem[] {
  return prepareFreeWorkoutExercises(value).map(exercise => ({
    name: exercise.name, muscle: exercise.muscle, sets: exercise.sets.length,
    defaultReps: exercise.defaultReps, defaultWeight: exercise.defaultWeight,
    timed: !!exercise.timed, restSeconds: exercise.restSeconds ?? 60, ...copyFitnessExerciseMetadata(exercise as unknown as Record<string, unknown>),
    ...(exercise.sets.some(set => set.type !== undefined) ? { setTypes: exercise.sets.map(set => set.type ?? 'working') } : {}),
  }));
}
export function freeWorkoutRouteItems(items: readonly FreeWorkoutItem[]) {
  return items.map(item => ({ ...item, ...(item.timed ? { durationSeconds: item.defaultReps } : {}) }));
}
export function saveFreeWorkoutBuilder(ownerId: string, items: readonly FreeWorkoutItem[]): void {
  const normalized = normalizeFreeWorkoutItems(freeWorkoutRouteItems(items));
  sessionStorage.setItem(builderKey(ownerId), JSON.stringify({ version: 1, ownerId, items: normalized }));
}
export function readFreeWorkoutBuilder(ownerId: string): FreeWorkoutItem[] {
  try {
    const saved = JSON.parse(sessionStorage.getItem(builderKey(ownerId)) ?? 'null');
    return saved?.version === 1 && saved.ownerId === ownerId ? normalizeFreeWorkoutItems(freeWorkoutRouteItems(saved.items)) : [];
  } catch { return []; }
}
export function clearFreeWorkoutBuilder(ownerId: string): void {
  try { sessionStorage.removeItem(builderKey(ownerId)); } catch { /* Clearing setup does not change a running session. */ }
}
/** A saved session becomes an editable proposal; it never completes or starts a workout. */
export function workoutHistoryProposal(value: unknown): FreeWorkoutItem[] {
  if (!Array.isArray(value)) return [];
  const proposal = value.slice(0, LIMITS.exercises).flatMap(item => {
    if (!item || typeof item !== 'object' || !Array.isArray(item.sets)) return [];
    const performed = item.sets.slice(0, LIMITS.sets).filter((set: { done?: unknown }) => set?.done === true);
    if (!performed.length) return [];
    const last = performed[performed.length - 1];
    try {
      return normalizeFreeWorkoutItems([{ name: item.name, muscle: item.muscle ?? '', sets: performed.length,
        defaultReps: last.reps, defaultWeight: last.weight, timed: item.timed === true, ...copyFitnessExerciseMetadata(item),
        ...(performed.some((set: { type?: string }) => set.type !== undefined) ? { setTypes: performed.map((set: { type?: string }) => set.type ?? 'working') } : {}),
        ...(item.timed === true ? { durationSeconds: last.reps } : {}) }]);
    } catch { return []; }
  });
  return proposal;
}
